import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box,
  Autocomplete,
  CircularProgress,
  Divider,
  Tooltip,
  Chip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const EditModal = ({
  open,
  onClose,
  item,
  group_code,
  project_code,
  apiUrl,
  onSave,
  semiProductMode,
}) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [validationError, setValidationError] = useState("");
  useEffect(() => {
    if (open && item) {
      loadMaterials();
      loadExistingBomItems();
      setValidationError("");
    }
  }, [open, item]);
  const loadMaterials = async () => {
    setLoading(true);
    try {
      const endpoint = semiProductMode ? "bringSemi" : "bringAll";
      const response = await axios.get(
        `${apiUrl}/api/material/${endpoint}?group_code=${group_code}&project_code=${project_code}`
      );
      setMaterials(response.data || []);
    } catch (error) {
      console.error("Error loading materials:", error);
      setValidationError("자재 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };
  const loadExistingBomItems = async () => {
    try {
      const existingItems = item.existingBomItems || [];
      if (existingItems.length > 0) {
        const formattedItems = existingItems.map((bomItem) => ({
          id: `existing_${bomItem.material_id}`,
          material_id: bomItem.material_id,
          material_name: bomItem.material_name || bomItem.item_name,
          quantity: bomItem.quantity || "1",
          qty_unit: bomItem.qty_unit || bomItem.default_unit || "EA",
          isExisting: true,
          isSemi: bomItem.category === "semi" || bomItem.item_type === 0,
        }));
        setItems(formattedItems);
      } else {
        setItems([createNewItem()]);
      }
    } catch (error) {
      console.error("Error loading existing BOM items:", error);
      setItems([createNewItem()]);
    }
  };
  const createNewItem = () => ({
    id: `new_${Date.now()}`,
    material_id: "",
    material_name: "",
    quantity: "1",
    qty_unit: "EA",
    isExisting: false,
    isSemi: false,
  });
  const handleAddItem = () => {
    setItems((prev) => [...prev, createNewItem()]);
    setValidationError("");
  };
  const handleDeleteItem = (id) => {
    if (items.length <= 1) return;
    setItems((prev) => prev.filter((item) => item.id !== id));
    setValidationError("");
  };
  const handleItemChange = (id, field, value) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          if (field === "material_id") {
            const selectedMaterial = materials.find(
              (m) => m.material_id === value
            );
            return {
              ...item,
              [field]: value,
              material_name:
                selectedMaterial?.material_name ||
                selectedMaterial?.item_name ||
                "",
              isSemi:
                selectedMaterial?.category === "semi" ||
                selectedMaterial?.item_type === 0,
              qty_unit: selectedMaterial?.default_unit || "EA",
            };
          }
          return { ...item, [field]: value };
        }
        return item;
      })
    );
    setValidationError("");
  };
  const validateItems = () => {
    if (items.length === 0) {
      return "최소 1개 이상의 자재를 등록해야 합니다.";
    }
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.material_id) {
        return `${i + 1}번째 항목: 자재를 선택해주세요.`;
      }
      if (!item.quantity || item.quantity === "") {
        return `${i + 1}번째 항목: 수량을 입력해주세요.`;
      }
      const quantityNum = parseFloat(item.quantity);
      if (isNaN(quantityNum)) {
        return `${i + 1}번째 항목: 수량은 숫자여야 합니다.`;
      }
      if (quantityNum <= 0) {
        return `${i + 1}번째 항목: 수량은 0보다 커야 합니다.`;
      }
      if (!item.qty_unit || item.qty_unit.trim() === "") {
        return `${i + 1}번째 항목: 단위를 입력해주세요.`;
      }
    }
    const materialIds = items.map((item) => item.material_id);
    const uniqueMaterialIds = new Set(materialIds);
    if (uniqueMaterialIds.size !== items.length) {
      const duplicates = materialIds.filter(
        (id, index) => materialIds.indexOf(id) !== index
      );
      const duplicateMaterial = materials.find(
        (m) => m.material_id === duplicates[0]
      );
      return `중복된 자재가 있습니다: ${
        duplicateMaterial?.material_name ||
        duplicateMaterial?.item_name ||
        "알 수 없음"
      }`;
    }
    return null;
  };
  const handleSave = async () => {
    const validationErrorMsg = validateItems();
    if (validationErrorMsg) {
      setValidationError(validationErrorMsg);
      return;
    }
    setSaving(true);
    setValidationError("");
    try {
      const endpoint = semiProductMode ? "updateSemiAll" : "updateAll";
      const url = `${apiUrl}/api/bom/${endpoint}`;

      const payload = {
        group_code,
        project_code,
        item_id: item.parent_item_id,
        data: items.map((item) => ({
          material_id: item.material_id,
          quantity: parseFloat(item.quantity),
          qty_unit: item.qty_unit.trim(),
        })),
      };
      const response = await axios.post(url, payload);
      if (response.data.success) {
        onSave({
          success: true,
          message: response.data.message || "BOM이 업데이트되었습니다.",
          item_id: response.data.item_id,
        });
        onClose();
      } else {
        setValidationError(response.data.message || "업데이트에 실패했습니다.");
      }
    } catch (error) {
      let errorMessage = "BOM 업데이트 중 오류가 발생했습니다.";
      if (error.response) {
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMessage = "잘못된 요청입니다. 입력값을 확인해주세요.";
        } else if (error.response.status === 404) {
          errorMessage = "제품을 찾을 수 없습니다.";
        } else if (error.response.status === 500) {
          errorMessage = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
        }
      } else if (error.request) {
        errorMessage = "서버와 연결할 수 없습니다. 네트워크를 확인해주세요.";
      }
      setValidationError(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  const getMaterialLabel = (material) => {
    const isSemi = material.category === "semi" || material.item_type === 0;
    return `${material.material_name || material.item_name}${
      isSemi ? " (반제품)" : ""
    }`;
  };
  const checkIsSemi = (material) => {
    return material?.category === "semi" || material?.item_type === 0;
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6">
          BOM 관리 - {item?.parent_item_name || ""}
          {semiProductMode && " (반제품 모드)"}
        </Typography>
        <IconButton edge="end" onClick={onClose} disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {validationError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setValidationError("")}
          >
            {validationError}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            기존 자재를 수정하거나 새로운 자재를 추가할 수 있습니다.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {items.map((itemData, index) => (
              <Grid
                container
                spacing={2}
                key={itemData.id}
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: itemData.isExisting
                    ? "#f5f5f5"
                    : "transparent",
                  borderRadius: 1,
                  border: !itemData.material_id ? "1px solid #f44336" : "none",
                }}
              >
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    options={materials}
                    getOptionLabel={(option) =>
                      option.material_name || option.item_name || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.material_id === value.material_id
                    }
                    onChange={(_, newValue) =>
                      handleItemChange(
                        itemData.id,
                        "material_id",
                        newValue?.material_id || ""
                      )
                    }
                    value={
                      materials.find(
                        (m) => m.material_id === itemData.material_id
                      ) || null
                    }
                    renderOption={(props, option) => {
                      const isSemi = checkIsSemi(option);
                      return (
                        <Box
                          component="li"
                          {...props}
                          sx={{
                            display: "flex !important",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {isSemi && (
                            <Chip
                              label="반제품"
                              size="small"
                              color="secondary"
                              sx={{ flexShrink: 0 }}
                            />
                          )}
                          <Typography variant="body2">
                            {option.material_name || option.item_name}
                          </Typography>
                        </Box>
                      );
                    }}
                    renderInput={(params) => {
                      const selectedMaterial = materials.find(
                        (m) => m.material_id === itemData.material_id
                      );
                      const isSemiSelected = checkIsSemi(selectedMaterial);

                      return (
                        <TextField
                          {...params}
                          label="자재 / 반제품"
                          variant="outlined"
                          fullWidth
                          required
                          error={!itemData.material_id}
                          helperText={
                            !itemData.material_id ? "자재를 선택하세요" : ""
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                {isSemiSelected && itemData.material_id && (
                                  <Chip
                                    label="반제품"
                                    size="small"
                                    color="secondary"
                                    sx={{
                                      height: 24,
                                      fontSize: "0.75rem",
                                    }}
                                  />
                                )}
                                {params.InputProps.startAdornment}
                              </Box>
                            ),
                          }}
                        />
                      );
                    }}
                    disabled={saving}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="수량"
                    variant="outlined"
                    fullWidth
                    required
                    value={itemData.quantity}
                    onChange={(e) =>
                      handleItemChange(itemData.id, "quantity", e.target.value)
                    }
                    type="number"
                    InputProps={{
                      inputProps: { min: 0.01, step: 0.01 },
                    }}
                    error={
                      !itemData.quantity ||
                      isNaN(parseFloat(itemData.quantity)) ||
                      parseFloat(itemData.quantity) <= 0
                    }
                    helperText={
                      !itemData.quantity
                        ? "수량을 입력하세요"
                        : isNaN(parseFloat(itemData.quantity))
                        ? "숫자를 입력하세요"
                        : parseFloat(itemData.quantity) <= 0
                        ? "0보다 큰 값"
                        : ""
                    }
                    disabled={saving}
                  />
                </Grid>
                <Grid item xs={4} md={2}>
                  <TextField
                    label="단위"
                    variant="outlined"
                    fullWidth
                    required
                    value={itemData.qty_unit}
                    onChange={(e) =>
                      handleItemChange(itemData.id, "qty_unit", e.target.value)
                    }
                    error={
                      !itemData.qty_unit || itemData.qty_unit.trim() === ""
                    }
                    helperText={
                      !itemData.qty_unit || itemData.qty_unit.trim() === ""
                        ? "단위 입력"
                        : ""
                    }
                    disabled={saving}
                  />
                </Grid>
                <Grid
                  item
                  xs={2}
                  md={2}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Tooltip
                    title={
                      items.length > 1
                        ? "삭제"
                        : "최소 한 개의 항목이 필요합니다"
                    }
                  >
                    <span>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteItem(itemData.id)}
                        disabled={items.length <= 1 || saving}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
                {itemData.isExisting && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      기존 항목 (수정 가능)
                    </Typography>
                  </Grid>
                )}
              </Grid>
            ))}

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={saving}
              >
                자재 추가
              </Button>
            </Box>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "flex-end" }}>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? "저장 중..." : "저장"}
        </Button>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          취소
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditModal;
